//changed due to package upgrade
import AppsHeader from '@projectK/components/AppsHeader';
import {
  alpha,
  Box,
  Button,
  FormControl,
  Select,
  useMediaQuery,
  useTheme,
  MenuItem,
  Typography,
} from '@mui/material';

import React, { useEffect, useState,useRef } from 'react';
import { useIntl } from 'react-intl';
import AppsContent from '@projectK/components/AppsContent';
import AppsPagination from '@projectK/components/AppsPagination';
import AppSearchBar from '@projectK/components/AppSearchBar';
import {
  WaiverLogTable,
  WaiversLogFilter,
} from '@projectK/modules/Admin/WaiversLog';
import AppInfoView from '@projectK/components/AppInfoView';
import { Fonts } from '@projectK/constants/AppEnums';
import jwtAxios from '@projectK/services/auth/JWT';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppsContainer from '@projectK/components/AppsContainer';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import { FileDownloadOutlined as FileDownloadOutlinedIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import {
  useRolePermissions,
  hasPermission,
} from '@projectK/context/RolesProvider';

const ButtonWithStyles = ({ children, onClick, sxProps }) => (
  <Button
    variant='outlined'
    size='medium'
    color='primary'
    onClick={onClick}
    sx={{
      position: 'relative',
      ml: 2.5,
      minWidth: 100,
      borderColor: sxProps.borderColor,
      '&:hover': {
        borderColor: alpha(sxProps.borderColor, 0.8),
      },
      color: sxProps.color,
      ...sxProps.additionalStyles,
    }}
  >
    {children}
  </Button>
);

const WaiversLog = () => {
  const isMobile = useMediaQuery('(max-width:755px)');
  const { verbiageSettings } = useApplicationSettings();
  const companyTheme = CompanyTheme();
  const { messages } = useIntl();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const [page, setPage] = useState(0);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = useState(20);
  const [titleSorting, setTitleSorting] = useState(null);
  const [descriptionSorting, setDescriptionSorting] = useState(null);
  const [userSorting, setUserSorting] = useState(null);
  const [dateSorting, setDateSorting] = useState(null);
  const [userFilter, setUserFilter] = useState('');
  const { rolePermissions } = useRolePermissions();
  const [signedDateFilter, setSignedDateFilter] = useState('');
  const abortControllerRef = useRef(null)
  const [exportLoading, setExportLoading] = useState(false);
  const handleChangePerPage = (event) => {
    const newPerPage = event.target.value;
    setPerPage(newPerPage);
    setPage(0);
  };

  const onSearchCustomer = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };
  const onPageChange = (event, value) => {
    setPage(value);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      const response = await jwtAxios.get('/waiver-settings/signed', {
        params: {
          search,
          page,
          perPage,
          filters: {
            userId: userFilter,
            created_at: signedDateFilter,
          },
          sortings: {
            waiver_name: titleSorting,
            description: descriptionSorting,
            user_name: userSorting,
            created_at: dateSorting,
          },
        },
        signal,
      });
      const { data } = response;
      setList(data.list);
      setTotal(data.total);
      data.list.length === 0 && page > 0 && setPage(page - 1);
      setLoading(false);
    } catch (error) {
    if (error.message !== 'canceled') {
        console.error('An error occurred:', error);
        setLoading(false);
    }
   }
  };
  useEffect(() => {
    fetchData();
   return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    search,
    page,
    perPage,
    userFilter,
    signedDateFilter,
    titleSorting,
    descriptionSorting,
    userSorting,
    dateSorting,
  ]);

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const response = await jwtAxios.get('/waiver-settings/export', {
        params: {
          search,
          filters: {
            userId: userFilter,
            created_at: signedDateFilter,
          },
          sortings: {
            waiver_name: titleSorting,
            description: descriptionSorting,
            user_name: userSorting,
            created_at: dateSorting,
          },
        },
        responseType: 'arraybuffer',
      });
      const blob = new Blob([response.data], { type: `application/csv` });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'Waivers_log.csv';
      link.click();
      setExportLoading(false);
    } catch (error) {
      console.error('An error occurred:', error);
      setExportLoading(false);
    }
  };

  return (
    <>
      <WaiversLogFilter
        userFilter={userFilter}
        signedDateFilter={signedDateFilter}
        setUserFilter={setUserFilter}
        setSignedDateFilter={setSignedDateFilter}
        verbiageSettings={verbiageSettings}
        setPage={setPage}
      />
      <br></br>
      <Typography
        variant='h4'
        className='mt-15'
        sx={{
          color: companyTheme ? companyTheme.primary.main : 'text.primary',
          fontWeight: Fonts.SEMI_BOLD,
        }}
      >
        {`${verbiageSettings?.waiver_name || 'Waiver'} Logs`}
      </Typography>
      <div className='event-search ps-h'>
        <AppsContainer fullView>
          <AppsHeader>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              width={1}
              justifyContent='space-between'
              className='main-box'
            >
              <Box display='flex' flexDirection='row' className='search-box'>
                <AppSearchBar
                  iconPosition='right'
                  overlap={false}
                  onChange={onSearchCustomer}
                  placeholder={messages['common.searchHere']}
                />
              </Box>
              <Box
                display='flex'
                flexDirection='row'
                alignItems='center'
                className='button-boxes'
              >
                {hasPermission(
                  rolePermissions,
                  'Company Settings - Waiver Logs',
                  'export',
                ) && (
                  <ButtonWithStyles
                    className='down-btn'
                    onClick={handleExportCSV}
                    sxProps={{
                      borderColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      color: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      textTransform: 'none',
                    }}
                  >
                    {isMobile ? (
                      <FileDownloadOutlinedIcon sx={{ mr: 1 }} />
                    ) : (
                      <>
                        {' '}
                        <FileDownloadOutlinedIcon sx={{ mr: 1 }} /> Export as
                        CSV{' '}
                      </>
                    )}
                  </ButtonWithStyles>
                )}
                {!isSmDown && (
                  <>                  <FormControl
                    variant='standard'
                    sx={{
                      borderRadius: 50,
                    }}
                    size='small'
                  >
                    <Select
                      value={perPage}
                      onChange={handleChangePerPage}
                      sx={{ marginLeft: '10px' }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={30}>30</MenuItem>
                      <MenuItem value={40}>40</MenuItem>
                      <MenuItem value={60}>60</MenuItem>
                      <MenuItem value={80}>80</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <AppsPagination
                    rowsPerPage={perPage}
                    count={total}
                    page={page}
                    onPageChange={onPageChange}
                  />
                </>
                )}
              </Box>
            </Box>
          </AppsHeader>
          <AppsContent
            className='evnt-s-table ps-events'
            sx={{
              paddingTop: 2.5,
              paddingBottom: 2.5,
            }}
          >
            <WaiverLogTable
              waiversLogData={list}
              loading={loading}
              fetchData={fetchData}
              verbiageSettings={verbiageSettings}
              titleSorting={titleSorting}
              descriptionSorting={descriptionSorting}
              userSorting={userSorting}
              dateSorting={dateSorting}
              setTitleSorting={setTitleSorting}
              setDescriptionSorting={setDescriptionSorting}
              setUserSorting={setUserSorting}
              setDateSorting={setDateSorting}
              exportLoading={exportLoading}
            />
          </AppsContent>
          {!isSmUp && (
            <>            <FormControl
              variant='standard'
              sx={{
                borderRadius: 50,
              }}
              size='small'
              className='cols-count colc25'
            >
              <Select
                value={perPage}
                onChange={handleChangePerPage}
                sx={{ marginLeft: '10px', height: '34px' }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={60}>60</MenuItem>
                <MenuItem value={80}>80</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <AppsPagination
              className='cols-pagination'
              rowsPerPage={perPage}
              count={total}
              page={page}
              onPageChange={onPageChange}
            />
          </>
          )}
        </AppsContainer>
        <AppInfoView />
      </div>
    </>
  );
};

ButtonWithStyles.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  sxProps: PropTypes.shape({
    borderColor: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    additionalStyles: PropTypes.object,
  }).isRequired,
};

export default WaiversLog;
